/* src/components/pages/Deudascmf.jsx - Última Modificación: 06-02-2026 */
/* ------------------------ deudas de la cmf -------------------------- */

/* src/components/pages/Deudascmf.jsx */
/* ---------------- Deudas CMF Overwatch FINAL ---------------- */

import React, { useState, useCallback, useEffect } from "react";

import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";

import api from "../../services/api.js";

import { CustomToolbar } from "../datagrid/CustomToolbar";
import DataGridSkeleton from "../datagrid/DataGridSkeleton";

import { exportPdfAsync, downloadBlob } from "../utils/exportUtils";
import { normalizeRut } from "../utils/rutUtils";
import { formatFecha } from "../utils/dateUtils";
import { validarRut } from "../utils/rutValidator";

import { useAuth } from "../../context/AuthContext";
import { useFeedback } from "../../context/FeedbackContext";

import {
  RiFileSearchLine,
  RiFolderDownloadLine,
  RiShareBoxFill,
  RiFilePdf2Fill
} from "react-icons/ri";

/* ---------------- CSV STANDARD ---------------- */

const CSV_HEADERS = [
  { key: "id", label: "ID" },
  { key: "fecha", label: "Fecha" },
  { key: "rut", label: "RUT Consultado" },
  { key: "nombre", label: "Razón Social" },
];

const Deudascmf = () => {
  const { user } = useAuth();
  const { showFeedback } = useFeedback();

  /* ---------------- DataGrid ---------------- */

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ---------------- Server Pagination ---------------- */

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);

  /* ---------------- Consulta exacta ---------------- */

  const [rutBuscado, setRutBuscado] = useState("");

  /* ---------------- Export States ---------------- */

  const [generandoPdf, setGenerandoPdf] = useState(false);

  const [openCsvDialog, setOpenCsvDialog] = useState(false);
  const [selectedCsvFields, setSelectedCsvFields] = useState(
    CSV_HEADERS.map((f) => f.key)
  );

  /* =======================================================
     FETCH HISTORIAL (SOLO CONSULTAS DEL CLIENTE)
  ======================================================= */

  const fetchHistorial = useCallback(
    async ({ page = 0, pageSize = 20 } = {}) => {
      if (!user?.usuario) return;

      setLoading(true);

      try {
        const response = await api.post("/deudascmf/list", {
          RutCli: normalizeRut(user.usuario),
          page,
          pageSize,
        });

        if (response.data.success) {
          setRows(response.data.rows || []);
          setRowCount(response.data.total || 0);
        } else {
          setRows([]);
          setRowCount(0);
        }
      } catch (err) {
        showFeedback({
          message: "No fue posible obtener historial CMF",
          severity: "error",
        });

        setRows([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    },
    [user, showFeedback]
  );

  /* =======================================================
     CARGA INICIAL AUTOMÁTICA (Overwatch obligatorio)
  ======================================================= */

  useEffect(() => {
    fetchHistorial({ page: 0, pageSize });
  }, [fetchHistorial, pageSize]);

  /* =======================================================
     CONSULTAR RUT EXACTO → REGISTRA HISTORIAL + REFRESH
  ======================================================= */

  const handleConsultarRut = async () => {
    const rutInput = rutBuscado.trim();

    if (!rutInput) {
      showFeedback({
        message: "Debe ingresar un RUT para consultar",
        severity: "warning",
      });
      return;
    }

    /* ✅ Normalizar */
    const rutNormalizado = normalizeRut(rutInput);

    /* ✅ Validar módulo 11 */
    if (!validarRut(rutNormalizado)) {
      showFeedback({
        message: "RUT ingresado no es válido",
        severity: "error",
      });
      return;
    }

    try {
      await api.post("/deudascmf/buscar", {
        RutCli: normalizeRut(user.usuario),
        rutBuscado: rutNormalizado,
      });

      showFeedback({
        message: "Consulta registrada correctamente",
        severity: "success",
      });

      /* refrescar historial */
      setRutBuscado("");
      setPage(0);
      fetchHistorial({ page: 0, pageSize });
    } catch (err) {
      showFeedback({
        message: err.response?.data?.message || "Rut no encontrado en CMF",
        severity: "error",
      });
    }
  };

  /* =======================================================
     EXPORT PDF ASYNC (HISTORIAL COMPLETO)
  ======================================================= */

  const handleExportPdf = async () => {
    exportPdfAsync({
      endpointBase: "/deudascmf/pdf",
      payload: {
        RutCli: normalizeRut(user.usuario),
      },
      onStart: () => setGenerandoPdf(true),
      onFinish: () => setGenerandoPdf(false),
      onError: (msg) => {
        setGenerandoPdf(false);
        showFeedback({
          message: msg,
          severity: "error",
        });
      },
    });
  };

  /* =======================================================
     EXPORT CSV SERVER-SIDE (HISTORIAL)
  ======================================================= */

  const toggleCsvField = (key) => {
    setSelectedCsvFields((prev) =>
      prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [...prev, key]
    );
  };

  const handleExportCsv = async () => {
    try {
      const response = await api.post(
        "/deudascmf/csv",
        {
          RutCli: normalizeRut(user.usuario),
          fields: selectedCsvFields,
        },
        { responseType: "blob" }
      );

      downloadBlob(response.data, "deudas_cmf_historial.csv");
      setOpenCsvDialog(false);
    } catch (err) {
      showFeedback({
        message: "No fue posible exportar CSV",
        severity: "error",
      });
    }
  };

  /* =======================================================
     COLUMNS
  ======================================================= */

  const columns = [
    {
      field: "pdf",
      headerName: "PDF",
      width: 70,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",

      renderCell: (params) => {
        const { id } = params.row;

        const handleOpenPdf = async () => {
          if (!id) return;

          try {
            const response = await api.post(
              "/deudascmf/pdf-row",
              { id },
              { responseType: "blob" }
            );

            const pdfBlob = new Blob([response.data], {
              type: "application/pdf",
            });

            const url = window.URL.createObjectURL(pdfBlob);
            window.open(url, "_blank");

            setTimeout(() => {
              window.URL.revokeObjectURL(url);
            }, 1500);

          } catch (err) {
            const status = err.response?.status;

            /* ✅ PDF no disponible */
            if (status === 404) {
              showFeedback({
                message: "Este registro no tiene PDF disponible",
                severity: "warning",
              });
              return;
            }

            /* ❌ Error real */
            showFeedback({
              message: "No fue posible abrir el PDF",
              severity: "error",
            });
          }
        };

        return (
          <Button
            variant="contained"
            onClick={handleOpenPdf}
            disabled={!params.row.hasPdf}
            sx={{
              minWidth: 0,
              width: 34,
              height: 26,
              padding: 0,

              backgroundColor: (theme) => theme.custom.ui.primaryAction,
              color: (theme) => theme.custom.ui.primaryActionText,

              "&:hover": {
                backgroundColor: (theme) =>
                  theme.custom.ui.primaryActionHover,
              },
            }}
          >
            <RiFilePdf2Fill size={18} />
          </Button>
        );
      },
    },
    {
      field: "id",
      headerName: "ID",
      width: 90,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "fecha",
      headerName: "Actualizado",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => formatFecha(params.value),
    },
    {
      field: "rut",
      headerName: "RUT Consultado",
      width: 170,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "nombre",
      headerName: "Razón Social",
      flex: 1,
      minWidth: 300,
    },
  ];

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <Box sx={{ width: "100%" }}>
      {/* CONTROLES */}
      <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
        <TextField
          label="Consultar Rut"
          size="small"
          value={rutBuscado}
          onChange={(e) => setRutBuscado(e.target.value)}
          sx={{ minWidth: 180 }}
        />

        <Button
          variant="contained"
          startIcon={<RiFileSearchLine />}
          onClick={handleConsultarRut}
        >
          Consultar
        </Button>

        <Button
          variant="contained"
          startIcon={<RiFolderDownloadLine />}
          disabled={rows.length === 0}
          onClick={handleExportPdf}
        >
          PDF
        </Button>

        <Button
          variant="contained"
          startIcon={<RiShareBoxFill />}
          disabled={rows.length === 0}
          onClick={() => setOpenCsvDialog(true)}
        >
          CSV
        </Button>
      </Stack>

      {/* DATAGRID */}
      <Box sx={{ height: "calc(100vh - 220px)" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={({ page, pageSize }) => {
            setPage(page);
            setPageSize(pageSize);
            fetchHistorial({ page, pageSize });
          }}
          slots={{
            toolbar: CustomToolbar,
            loadingOverlay: DataGridSkeleton,
          }}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        />
      </Box>

      <Typography variant="caption" color="text.secondary">
        {rowCount.toLocaleString("es-CL")} consultas registradas
      </Typography>

      {/* PDF Dialog */}
      <Dialog open={generandoPdf}>
        <DialogContent>
          <Stack spacing={2} alignItems="center" py={2}>
            <CircularProgress />
            <Typography variant="body2">
              Generando reporte PDF…
            </Typography>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* CSV Dialog */}
      <Dialog open={openCsvDialog} onClose={() => setOpenCsvDialog(false)}>
        <DialogTitle>Exportar CSV</DialogTitle>
        <Divider />

        <DialogContent>
          <Stack spacing={0.5}>
            {CSV_HEADERS.map((f) => (
              <FormControlLabel
                key={f.key}
                control={
                  <Checkbox
                    checked={selectedCsvFields.includes(f.key)}
                    onChange={() => toggleCsvField(f.key)}
                  />
                }
                label={f.label}
              />
            ))}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenCsvDialog(false)}>Cancelar</Button>

          <Button
            variant="contained"
            disabled={selectedCsvFields.length === 0}
            onClick={handleExportCsv}
          >
            Exportar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Deudascmf;
